import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { resolveImg, formatDate } from "@/lib/blog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = typeof window !== "undefined" ? window.location.origin : "";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    axios.get(`${API}/blogs/${slug}`)
      .then(({ data }) => { setPost(data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-konattu-cream flex flex-col items-center justify-center gap-4" data-testid="blog-notfound">
        <p className="font-display text-2xl text-konattu-charcoal">Post not found.</p>
        <Link to="/blog" className="text-konattu-red font-mono text-sm tracking-widest">ALL POSTS</Link>
      </div>
    );
  }

  const cover = post ? resolveImg(post.cover_image) : null;
  const jsonLd = post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: cover || `${SITE}/konattu-logo.png`,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: post.author || "Konattu Group" },
    publisher: { "@type": "Organization", name: "Konattu Group", logo: { "@type": "ImageObject", url: `${SITE}/konattu-logo.png` } },
    mainEntityOfPage: `${SITE}/blog/${slug}`,
    keywords: (post.tags || []).join(", "),
  } : null;

  return (
    <main className="bg-konattu-cream min-h-screen" data-testid="blog-post-page">
      {post && <Seo title={post.title} description={post.excerpt} path={`/blog/${slug}`} image={cover} type="article" jsonLd={jsonLd} />}
      <div className="noise-overlay" />
      <div className="fixed top-0 inset-x-0 z-50 bg-konattu-cream/80 backdrop-blur-2xl border-b border-konattu-charcoal/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
          <Link to="/" data-testid="post-logo"><Logo /></Link>
          <Link to="/blog" data-testid="post-back" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-konattu-charcoal/70 hover:text-konattu-red transition-colors">
            <ArrowLeft size={14} /> ALL POSTS
          </Link>
        </div>
      </div>

      {status === "loading" ? (
        <div className="pt-[140px] max-w-3xl mx-auto px-6"><p className="font-body text-konattu-charcoal/60">Loading...</p></div>
      ) : (
        <article className="pt-[70px]">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 md:pt-24">
            <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.25em] text-konattu-charcoal/50 mb-6">
              {post.tags?.[0] && <span className="text-konattu-red">{post.tags[0].toUpperCase()}</span>}
              <span>{formatDate(post.created_at)}</span>
              <span>· {post.author}</span>
            </div>
            <h1 className="font-display font-extrabold text-konattu-charcoal tracking-tight leading-[1.02] text-4xl md:text-6xl">{post.title}</h1>
            {post.excerpt && <p className="mt-6 font-body text-lg md:text-xl text-konattu-charcoal/70 leading-relaxed">{post.excerpt}</p>}
          </div>

          {cover && (
            <div className="max-w-5xl mx-auto px-6 lg:px-10 mt-12">
              <div className="aspect-[16/9] overflow-hidden">
                <img src={cover} alt={post.title} className="w-full h-full object-cover" />
              </div>
            </div>
          )}

          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14 md:py-20 blog-content font-body text-konattu-charcoal/85 text-lg leading-relaxed">
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </div>
        </article>
      )}
      <Footer />
    </main>
  );
}
